import { MCP_TOOL_GROUPS_IN_USE, wikiTrustLevelSchema } from "@esi/shared";
import { mcpToolGroupCopy } from "../../admin/settings-groups.ts";

/**
 * The settings a project may override, as the settings page shows them: group, wording key and
 * editor. The API says which keys are overridable; a key it marks overridable but this list does
 * not know is not shown (complexity stays backstage).
 */
export type BehaviourKind =
  | "boolean"
  | "residency"
  | "classification"
  | "pages"
  | "guidelines"
  | "placement"
  | "reviewDays"
  | "domains"
  | "days"
  | "approval"
  | "trust"
  | "efforts"
  | "effort"
  | "models"
  | "egress";

export interface BehaviourDef {
  group: "answers" | "models" | "knowledge" | "wiki" | "mcp" | "web";
  /** `settings.behaviour.keys.<copy>` holds its label and help. */
  copy: string;
  kind: BehaviourKind;
}

export const BEHAVIOUR: Readonly<Record<string, BehaviourDef>> = {
  "answers.sourcesOnly": { group: "answers", copy: "sourcesOnly", kind: "boolean" },
  "models.residency": { group: "answers", copy: "residency", kind: "residency" },
  "projects.tasks": { group: "answers", copy: "tasks", kind: "boolean" },
  "memory.personal": { group: "answers", copy: "memoryPersonal", kind: "boolean" },
  "memory.project": { group: "answers", copy: "memoryProject", kind: "boolean" },
  "conversations.attachments": { group: "answers", copy: "attachments", kind: "boolean" },
  "conversations.artefacts": { group: "answers", copy: "artefacts", kind: "boolean" },
  "conversations.thinkingSummary": {
    group: "answers",
    copy: "thinkingSummary",
    kind: "boolean",
  },
  // Efforts and the exact-model picker; allowed models are the advanced narrowing.
  "models.allowed": { group: "models", copy: "modelsAllowed", kind: "efforts" },
  "models.defaultEffort": { group: "models", copy: "defaultEffort", kind: "effort" },
  "models.advancedPicker": { group: "models", copy: "advancedPicker", kind: "boolean" },
  "models.allowedModels": { group: "models", copy: "allowedModels", kind: "models" },
  "knowledge.defaultClassification": {
    group: "knowledge",
    copy: "defaultClassification",
    kind: "classification",
  },
  "knowledge.ocr": { group: "knowledge", copy: "ocr", kind: "boolean" },
  "knowledge.imageCaptions": { group: "knowledge", copy: "imageCaptions", kind: "boolean" },
  "knowledge.ocrMaxPages": { group: "knowledge", copy: "ocrMaxPages", kind: "pages" },
  "knowledge.declassification": {
    group: "knowledge",
    copy: "declassification",
    kind: "boolean",
  },
  "knowledge.generatedSources": {
    group: "knowledge",
    copy: "generatedSources",
    kind: "boolean",
  },
  "wiki.guidelines": { group: "wiki", copy: "guidelines", kind: "guidelines" },
  "wiki.placement": { group: "wiki", copy: "placement", kind: "placement" },
  "wiki.reviewAfterDays": { group: "wiki", copy: "reviewAfterDays", kind: "reviewDays" },
  "wiki.autoDraftSummaries": { group: "wiki", copy: "autoDraftSummaries", kind: "boolean" },
  "wiki.suggestions": { group: "wiki", copy: "wikiSuggestions", kind: "boolean" },
  // Who may approve and publish wiki changes (wiki-collaboration D9): a project only tightens.
  "wiki.trust": { group: "wiki", copy: "wikiTrust", kind: "trust" },
  // Narrow-only (mcp-write-tools D1, ADR 0018 §1): a project may only turn MCP writes and
  // tool groups off. A group with no tools yet has no row (settings-groups.ts).
  "mcp.writeTools": { group: "mcp", copy: "mcpWriteTools", kind: "boolean" },
  "wiki.mcpSelfPublish": { group: "mcp", copy: "mcpSelfPublish", kind: "boolean" },
  ...Object.fromEntries(
    MCP_TOOL_GROUPS_IN_USE.map((g) => [
      `mcp.tools.${g}`,
      { group: "mcp", copy: mcpToolGroupCopy(g), kind: "boolean" } satisfies BehaviourDef,
    ]),
  ),
  "tools.webSearch": { group: "web", copy: "webSearch", kind: "boolean" },
  "tools.webSearch.domains": { group: "web", copy: "webDomains", kind: "domains" },
  "tools.webSearch.maxAgeDays": { group: "web", copy: "webMaxAge", kind: "days" },
  "tools.webFetch": { group: "web", copy: "webFetch", kind: "boolean" },
  "tools.webFetch.approval": { group: "web", copy: "webFetchApproval", kind: "approval" },
  "tools.webFetch.domains": { group: "web", copy: "webFetchDomains", kind: "domains" },
  // Narrow-only per audience (settings-typed-controls D10): a project only makes it stricter.
  "tools.egress": { group: "web", copy: "egress", kind: "egress" },
  // Code execution (agent-turn D14, D17): on by default, narrow-only; its own floors policy.
  "tools.codeExecution": { group: "web", copy: "codeExecution", kind: "boolean" },
  "tools.codeExecution.egress": { group: "web", copy: "codeEgress", kind: "egress" },
};

/** `wiki.trust`, least strict first. */
export const WIKI_TRUST_LEVELS = wikiTrustLevelSchema.options;

/** `tools.webFetch.approval`, least strict first. */
export const WEB_FETCH_APPROVALS = ["provenance", "always"] as const;

export const GROUPS = ["answers", "models", "knowledge", "wiki", "mcp", "web"] as const;

export const PAGE_TYPES = ["summary", "concept", "entity", "other"] as const;

export interface Guidelines {
  language?: string;
  pageTypes: (typeof PAGE_TYPES)[number][];
  structure?: string;
}

/** The most pages one scan may read (`knowledge.ocrMaxPages`, the server's bound). */
export const MAX_OCR_PAGES = 2000;

/** The most pages a project may allow: never more than it inherits. */
export const pagesCeiling = (inherited: unknown) =>
  typeof inherited === "number" ? inherited : MAX_OCR_PAGES;

/** The most days `tools.webSearch.maxAgeDays` allows (the server's bound). */
export const MAX_AGE_DAYS = 3650;

/** The oldest web results a project may allow: never older than it inherits. */
export const maxAgeCeiling = (inherited: unknown) =>
  typeof inherited === "number" ? Math.min(inherited, MAX_AGE_DAYS) : MAX_AGE_DAYS;
